import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { API_ENDPOINTS } from "./api-endpoints";

export interface FeedbackMetadata {
  user_id: string;
  period: string;
  created_at: string;
}

export interface FeedbackDashboardFilters {
  quarter?: string;
  category?: string;
  segment?: string;
}

export interface FeedbackDashboard {
  net_promoter_score: number;
  average_rating: number;
  key_driver: string[];
  unique_respondents: number;
  total_responses: number;
  response_sentiment: { segment: string; count: number; percentage: number }[];
  rating_distribution: { rating: number; count: number; percentage: number }[];
  category_mentions: { category: string; count: number; percentage: number }[];
  word_cloud?: {
    terms: {
      text: string;
      count: number;
      sentiment_score: number;
      sentiment: "positive" | "neutral" | "negative";
    }[];
    sentiment_method: string;
  };
}

@Injectable({ providedIn: "root" })
export class FeedbackApi {
  constructor(private readonly http: HttpClient) {}

  getDashboard(
    filters: FeedbackDashboardFilters = {},
  ): Observable<FeedbackDashboard> {
    const params: Record<string, string> = {};
    if (filters.quarter) params.quarter = filters.quarter;
    if (filters.category) params.category = filters.category;
    if (filters.segment) params.segment = filters.segment;
    return this.http.get<FeedbackDashboard>(API_ENDPOINTS.feedback.dashboard, {
      params,
    });
  }

  getMetadata(): Observable<readonly FeedbackMetadata[]> {
    return this.http.get<readonly FeedbackMetadata[]>(
      API_ENDPOINTS.feedback.metadata,
    );
  }

  download(filters: FeedbackDashboardFilters = {}): Observable<Blob> {
    const params: Record<string, string> = {};
    if (filters.quarter) params.quarter = filters.quarter;
    if (filters.category) params.category = filters.category;
    if (filters.segment) params.segment = filters.segment;
    return this.http.get(API_ENDPOINTS.feedback.download, {
      params,
      responseType: "blob",
    });
  }

  hasSubmitted(period: string): Observable<boolean> {
    return this.http.get<boolean>(API_ENDPOINTS.feedback.submitted(period));
  }

  submitFeedback(period: string, questions: Record<string, unknown>[]) {
    return this.http.post(API_ENDPOINTS.feedback.submit, {
      period,
      feedback: { questions },
    });
  }
}
